
import { useReceiverStore } from '@/store/receiver/receiver.store'
import { useRateStore } from '@/store/xe/rate.store'
import { defineComponent } from 'vue'
import Countries from '../../../assets/files/countries.json'
import {
  MiniPhoneDTO,
  MiniPhoneDTOPhoneOperatorEnum,
  ReceiverDTO,
  SendMoneyDto,
  SendMoneyDtoTransactionTypeEnum,
  TransactionDTO,
  TransactionDTOTransactionStatusEnum
} from '@/models'
import BalanceAccount from '@/components/dashboard/BalanceAccount.vue'
import { useMobileStore } from '@/store/transactions/mobile.store'
import { useAccountStore } from '@/store/auth/account.store'
import { DeviseType } from '@/helpers/enum'
import { phoneNumberCM, regexMTN, regexOrange } from '@/helpers/constants'
import { formatAmount, receiverDisplayName } from '@/helpers/functions'
import ListContactModal from '@/components/dashboard/SendMoney/ListContactModal.vue'

// Étapes de vérification du nom affichées directement sous le récapitulatif d'envoi, une fois la
// transaction créée : 'idle' correspond au formulaire initial (bouton "Envoyer" actif).
type Phase =
  | 'idle'
  | 'waiting'
  | 'confirm'
  | 'processing'
  | 'success'
  | 'failed'
  | 'rejected'
  | 'timeout'
  | 'declined'
  | 'stillProcessing'

const POLL_INTERVAL_MS = 2500
// Filets de sécurité côté client si jamais rien ne bouge, avec une marge réelle au-dessus des
// délais serveur correspondants (app.name-check-claim-timeout-seconds = 120s,
// app.recipient-confirmation-timeout-seconds = 180s côté backend) : sans cette marge, le client
// peut déclencher son propre "stillProcessing" au moment exact où le serveur résout légitimement
// la phase, affichant à tort un message d'attente pour une transaction qui vient de réussir.
const NAME_CHECK_MAX_WAIT_MS = 3 * 60 * 1000
const CONFIRMATION_MAX_WAIT_MS = 5 * 60 * 1000
const DEPOSIT_MAX_WAIT_MS = 2 * 60 * 1000

const TERMINAL_PHASES: Phase[] = ['success', 'failed', 'rejected', 'timeout', 'declined', 'stillProcessing']

// Préfixe de transactionInfo posé par le backend quand la confirmation automatique d'un destinataire
// déjà vérifié est refusée (solde insuffisant, plafond dépassé) : voir autoConfirmVerifiedRecipient.
const AUTO_CONFIRM_REFUSED_PREFIX = 'Confirmation automatique refusee'

export default defineComponent({
  name: 'SendMoney',
  components: {
    BalanceAccount,
    ListContactModal
  },
  beforeMount: function () {
    this.receiverStore.getReceivers().then(() => {
      if (this.mobileStore.amountCAD <= 0) this.mobileStore.setAmountXAF(1000)
      if (this.$route.params.receiver) {
        const receiver = this.receiverStore.getReceiverById(this.$route.params.receiver as unknown as number)
        this.receiverSelected(receiver)
      }
    })
  },
  computed: {
    mobileStore: function () {
      return useMobileStore()
    },
    accountStore: function () {
      return useAccountStore()
    },
    rateStore: function () {
      return useRateStore()
    },
    receiverStore: function () {
      return useReceiverStore()
    },
    isKycApproved: function (): boolean {
      const status = this.accountStore.accountData.kycVerificationStatus
      return status === 'APPROVED' || status === 'VERIFIED' || status === 'COMPLETED'
    },
    balanceInsufficient: function (): string | string[] {
      if (this.mobileStore.amountCAD > this.accountStore.accountData.balance!) {
        return this.$t('balanceInsufficient') as string
      }
      return []
    },
    roundAmountXAF(): string {
      if (this.mobileStore.amountXAF % 5 === 0) return `${this.mobileStore.amountXAF}`
      const amountRounded: number = Number(5 - (this.mobileStore.amountXAF % 5)) + Number(this.mobileStore.amountXAF)
      return amountRounded.toFixed(2)
    },
    roundAmountCAD(): string {
      return (Number(this.roundAmountXAF) / this.rateStore.currentRate).toFixed(2)
    },
    amountXAFErrorMsg: function (): string | string[] {
      if (this.mobileStore.amountXAF < 1000) {
        return this.$t('minAmountMsg') as string
      }
      if (this.mobileStore.amountXAF > 500000) {
        return this.$t('maxAmountMsg') as string
      }
      return []
    },
    isTerminalPhase(): boolean {
      return TERMINAL_PHASES.includes(this.phase)
    },
    // Fige le formulaire dès que la transaction est créée : sa modification n'aurait plus aucun
    // effet (la transaction existe déjà côté serveur avec les valeurs envoyées).
    formLocked(): boolean {
      return this.phase !== 'idle'
    }
  },
  watch: {
    phoneNumber(val: string) {
      if (val) {
        if (regexOrange.test(val)) this.mobileStore.sendMoneyMethod = this.phoneOperator.ORANGECAMEROUN
        if (regexMTN.test(val)) this.mobileStore.sendMoneyMethod = this.phoneOperator.MTNCAMEROON
      }
    }
  },
  data: function () {
    return {
      countries: Countries,
      selectedCountry: { name: 'Cameroon', dial_code: '+237', code: 'CM', flag: '🇨🇲' },
      phoneNumber: '',
      receiveName: '',
      phoneOperator: MiniPhoneDTOPhoneOperatorEnum,
      existingReceiver: false,
      showContactList: false,
      pendingTransactionId: null as number | null,
      phase: 'idle' as Phase,
      recipientName: '',
      confirmError: '',
      autoProcessing: false,
      declineReason: '',
      balanceRefreshed: false,
      actionLoading: false,
      pollHandle: null as ReturnType<typeof setInterval> | null,
      phaseStartedAt: 0,
      addRecipient: true,
      loading: false,
      errorMessage: '',
      showErrorMessage: false,
      deviseType: DeviseType,
      saveReceiver: false,
      isValid: true,
      phoneNumberRules: [(v: string) => phoneNumberCM.test(v) || this.$t('phoneNumberNotValid')],
      receiverNameRules: [
        (v: string) => !!v || this.$t('requiredField'),
        (v: string) => {
          if (!v) return true
          const words = v.trim().split(' ')
          return words.length >= 2 || this.$t('receiverNameRequired')
        }
      ],
      formatAmount,
      receiverDisplayName
    }
  },
  destroyed: function () {
    this.stopPolling()
    this.receiverStore.receiver = null
    this.mobileStore.setAmountXAF(1000)
  },
  methods: {
    receiverSelected: function (receiverDto: ReceiverDTO | null) {
      this.receiverStore.receiver = receiverDto
      if (receiverDto != null) {
        this.existingReceiver = true
        this.addRecipient = false
        this.saveReceiver = false
        this.receiveName = receiverDisplayName(receiverDto)
        if (receiverDto.phones?.length! > 1) {
          this.showContactList = true
        } else {
          this.changeReceiver(receiverDto)
        }
      } else {
        this.cleanForm()
      }
    },

    cleanForm: function () {
      this.receiveName = ''
      this.phoneNumber = ''
      this.selectedCountry = { name: 'Cameroon', dial_code: '+237', code: 'CM', flag: '🇨🇲' }
      this.existingReceiver = false
      this.addRecipient = true
      this.receiverStore.receiver = null
    },

    // Change fields when receiver is selected
    changeReceiver: function (receiverDto: ReceiverDTO) {
      this.selectedCountry = this.countries.find(
        country => country.dial_code === '+' + receiverDto.phones![0].dialCode!
      )!
      this.phoneNumber = receiverDto.phones![0].phoneNumber!.trim()
      this.mobileStore.sendMoneyMethod = receiverDto.phones![0].phoneOperator!
    },

    // Change fields when contact is selected
    selectedContact: function (contact: MiniPhoneDTO) {
      this.selectedCountry = this.countries.find(country => country.dial_code === '+' + contact.dialCode)!
      this.phoneNumber = contact.phoneNumber!.trim()
      this.mobileStore.sendMoneyMethod = contact.phoneOperator!
      this.showContactList = false
    },

    // Send money
    sendMoney: async function () {
      if (this.mobileStore.amountXAF < 1000) {
        this.errorMessage = this.$t('sendMoneyAmountError') as string
        this.showErrorMessage = true
        return
      }

      this.loading = true

      let splitted: string[] = []
      if (this.receiveName.includes(' ')) {
        splitted = this.receiveName.split(' ')
      }

      const sendMoneyDto: SendMoneyDto = {
        addReciever: this.addRecipient,
        amountCDA: Number(this.roundAmountCAD),
        amountXFA: Math.round(Number(this.roundAmountXAF)),
        transactionFee: 0,
        transactionRate: this.rateStore.currentRate,
        transactionType: SendMoneyDtoTransactionTypeEnum.MOBILETRANSFERE,
        receiverDTO: {
          firstName: splitted.length > 1 ? splitted[0] : this.receiveName,
          lastName:
            splitted.length >= 2 ? this.receiveName.substring(splitted[0].length + 1, this.receiveName.length) : ' ',
          phone: {
            dialCode: this.selectedCountry.dial_code.replace('+', ''),
            phoneNumber: this.phoneNumber.toString(),
            phoneOperator: this.mobileStore.sendMoneyMethod
          },
          saved: this.saveReceiver
        }
      }

      if (this.receiverStore.receiver != null) sendMoneyDto.receiverDTO!.id = this.receiverStore.receiver.id

      await this.mobileStore
        .sendMoney(sendMoneyDto)
        .then(async response => {
          await this.accountStore.refreshData()
          this.showErrorMessage = false
          // La transaction est créée mais pas encore terminée : le dépôt réel n'a lieu qu'après
          // vérification du nom du destinataire par l'agent USSD et confirmation du client
          // (voir startPolling ci-dessous), donc pas de message de succès statique ici. Le
          // formulaire reste affiché tel quel (figé, voir formLocked) tant que ce suivi est en
          // cours : cleanForm() n'a lieu qu'à la fermeture finale (voir resetFlow).
          this.pendingTransactionId = response.data.id
          // Destinataire déjà vérifié par le passé : le backend confirme lui-même (aucune vérification
          // USSD ni clic de confirmation), on adapte donc seulement le libellé d'attente.
          const knownReceiver = this.receiverStore.receiver
          this.autoProcessing = Boolean(knownReceiver?.nameVerified && knownReceiver?.verifiedRecipientName)
          this.declineReason = ''
          this.balanceRefreshed = false
          this.phase = 'waiting'
          this.recipientName = ''
          this.phaseStartedAt = Date.now()
          this.startPolling()
        })
        .catch(err => {
          if (err.response.status === 400) {
            this.errorMessage = err.response.data.title as string
            this.showErrorMessage = true
          }
          if (err.response.status === 403) {
            this.errorMessage = err.response.data.title as string
            this.showErrorMessage = true
          }
          if (err.response.status === 500) {
            this.errorMessage = err.response.data.title as string
            this.showErrorMessage = true
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    roundedAndConvertAmount() {
      if (this.mobileStore.amountXAF % 5 === 0) {
        this.mobileStore.amountXAF = Number(this.mobileStore.amountXAF).toFixed(2) as unknown as number
        return
      }
      const amountRounded = Number(5 - (this.mobileStore.amountXAF % 5) + Number(this.mobileStore.amountXAF))
      this.mobileStore.setAmountXAF(amountRounded)
    },

    startPolling(): void {
      this.stopPolling()
      this.pollHandle = setInterval(() => this.poll(), POLL_INTERVAL_MS)
      this.poll()
    },
    stopPolling(): void {
      if (this.pollHandle) {
        clearInterval(this.pollHandle)
        this.pollHandle = null
      }
    },
    async poll(): Promise<void> {
      if (!this.pendingTransactionId) return
      try {
        const transaction = await this.mobileStore.getTransactionStatus(this.pendingTransactionId)
        this.applyStatus(transaction)
      } catch (err) {
        // Erreur réseau isolée sur un poll : le prochain cycle retentera, on ne bloque pas l'UI.
        // On vérifie quand même le filet de sécurité temporel ici : sans ça, une panne réseau
        // soutenue empêcherait applyStatus() (seul autre appelant) de jamais s'exécuter, et
        // l'écran resterait bloqué indéfiniment sur la phase courante.
        this.checkClientSideTimeout()
      }
    },
    applyStatus(transaction: TransactionDTO): void {
      const status = transaction.transactionStatus

      switch (status) {
        case TransactionDTOTransactionStatusEnum.AWAITING_CLIENT_CONFIRMATION:
          if (this.phase === 'waiting') {
            this.recipientName = transaction.recipientNameFromUssd ?? ''
            this.confirmError = ''
            this.setPhase('confirm')
          }
          break
        case TransactionDTOTransactionStatusEnum.CONFIRMED:
          // Le compte est débité dès CONFIRMED : pour un destinataire déjà vérifié c'est le backend
          // qui confirme (aucun clic, donc pas de rafraîchissement dans onConfirm) — on met à jour
          // le solde affiché dès qu'on observe ce statut.
          this.refreshBalanceOnce()
          if (this.phase !== 'processing') {
            this.setPhase('processing')
          }
          break
        case TransactionDTOTransactionStatusEnum.DONE:
          this.refreshBalanceOnce()
          this.setPhase('success')
          this.stopPolling()
          break
        case TransactionDTOTransactionStatusEnum.FAILURE:
        case TransactionDTOTransactionStatusEnum.MANUAL_PAYMENT_REQUIRED:
          this.setPhase('failed')
          this.stopPolling()
          break
        case TransactionDTOTransactionStatusEnum.REJECTED: {
          // Confirmation automatique refusée par le backend (solde/plafond) : on affiche la raison.
          // Sinon, si on n'a pas nous-même déclenché de rejet (onReject arrête déjà le polling avant
          // qu'un tel statut puisse être vu ici), c'est l'expiration du délai de confirmation.
          const info = transaction.transactionInfo ?? ''
          if (info.startsWith(AUTO_CONFIRM_REFUSED_PREFIX)) {
            this.declineReason = info.substring(AUTO_CONFIRM_REFUSED_PREFIX.length).replace(/^\s*:\s*/, '')
            this.setPhase('declined')
          } else if (this.phase !== 'rejected') {
            this.setPhase('timeout')
          }
          this.stopPolling()
          break
        }
        default:
          // INIT / PEDDINT / NAME_CHECK_IN_PROGRESS : toujours en attente du nom, rien à faire
          break
      }

      this.checkClientSideTimeout()
    },
    refreshBalanceOnce(): void {
      if (this.balanceRefreshed) return
      this.balanceRefreshed = true
      this.accountStore.refreshData()
    },
    setPhase(next: Phase): void {
      if (this.phase !== next) {
        this.phase = next
        this.phaseStartedAt = Date.now()
      }
    },
    checkClientSideTimeout(): void {
      const elapsed = Date.now() - this.phaseStartedAt
      const budgetByPhase: Partial<Record<Phase, number>> = {
        waiting: NAME_CHECK_MAX_WAIT_MS,
        confirm: CONFIRMATION_MAX_WAIT_MS,
        processing: DEPOSIT_MAX_WAIT_MS
      }
      const budget = budgetByPhase[this.phase]
      if (budget && elapsed > budget) {
        this.setPhase('stillProcessing')
        this.stopPolling()
      }
    },
    async onConfirm(): Promise<void> {
      if (!this.pendingTransactionId) return
      this.actionLoading = true
      this.confirmError = ''
      try {
        // C'est cet appel qui débite le compte (le débit n'a plus lieu à la création) : on
        // rafraîchit donc le solde ici, pas avant, pour que l'affichage reste exact.
        await this.mobileStore.confirmRecipient(this.pendingTransactionId)
        this.balanceRefreshed = true
        await this.accountStore.refreshData()
        this.setPhase('processing')
      } catch (err: any) {
        // Une erreur ici (ex. solde devenu insuffisant entre-temps) ne doit pas être un cul-de-sac :
        // on reste sur l'écran de confirmation, avec le message d'erreur, pour que le client puisse
        // recharger son compte et recliquer Confirmer avant l'expiration du délai.
        this.confirmError = err.response?.data?.title ?? (this.$t('depositFailedTitle') as string)
      } finally {
        this.actionLoading = false
      }
    },
    async onReject(): Promise<void> {
      if (!this.pendingTransactionId) return
      this.actionLoading = true
      try {
        await this.mobileStore.rejectRecipient(this.pendingTransactionId)
        this.setPhase('rejected')
        this.stopPolling()
      } catch (err) {
        this.setPhase('stillProcessing')
      } finally {
        this.actionLoading = false
      }
    },
    // Fermeture du récapitulatif une fois une phase terminale atteinte (succès, échec, rejet,
    // expiration) : remet le formulaire à zéro pour un nouvel envoi.
    resetFlow(): void {
      this.stopPolling()
      this.phase = 'idle'
      this.pendingTransactionId = null
      this.recipientName = ''
      this.confirmError = ''
      this.autoProcessing = false
      this.declineReason = ''
      this.balanceRefreshed = false
      this.cleanForm()
    }
  }
})
